import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Building2, 
  Wrench, 
  AlertTriangle, 
  Compass, 
  DoorOpen, 
  Home, 
  Shield, 
  Search, 
  Truck, 
  Layers
} from 'lucide-react';

const services = [
  {
    id: 1,
    title: "Structural Steel & Stairs",
    icon: Building2,
    description: "Advanced structural steel fabrication involves precision cutting, welding, and assembling of steel components using modern technology for high-strength, custom-built structures. Structural Stairs - Crafted for both strength and contemporary style, our structural steel staircases are the perfect fit for sophisticated, high-end environments."
  },
  {
    id: 2,
    title: "Metal Works & Wrought Iron",
    icon: Wrench,
    description: "We offer expert metal works and intricate wrought iron craftsmanship tailored for architectural steel projects, combining traditional artistry with modern engineering to create visually striking, structurally sound elements such as gates, railings, staircases, and decorative features."
  },
  {
    id: 3,
    title: "Engineering Rectification Works",
    icon: AlertTriangle,
    description: "We provide comprehensive engineering rectification works, addressing structural deficiencies, design flaws, and construction errors with precision solutions that restore safety, functionality, and compliance to engineering standards across residential, commercial, and industrial projects."
  },
  {
    id: 4,
    title: "Architectural Steel Work - Curved, Design to Build",
    icon: Compass,
    description: "We specialize in architectural steel work with a focus on curved and custom-designed elements, offering end-to-end solutions from concept and design to fabrication and installation, delivering striking, structurally sound features that blend artistic vision with engineering excellence."
  },
  {
    id: 5,
    title: "Automatic Gates, Balustrade & Feature Fencing",
    icon: DoorOpen,
    description: "We design, fabricate, and install premium automatic gates, balustrades, and feature fencing, expertly combining advanced automation technology with durable materials and elegant designs to provide superior security, seamless functionality, and enhanced visual appeal."
  },
  {
    id: 6,
    title: "Custom Fabrications for Residential and Commercial",
    icon: Home,
    description: "We deliver bespoke custom fabrications for residential and commercial buildings, meticulously crafting steel components tailored to specific architectural and structural requirements, ensuring seamless integration, exceptional durability, and unique design solutions."
  },
  {
    id: 7,
    title: "Certified Welding Service",
    icon: Shield,
    description: "Our certified welding services strictly adhere to the AS1554 standard, specializing in Structural Steel Welding (SP), where we combine advanced techniques, skilled craftsmanship, and rigorous quality control to deliver welds that meet the highest safety, strength, and durability requirements."
  },
  {
    id: 8,
    title: "Welding Inspection - VT & NDT",
    icon: Search,
    description: "Our welding inspection services are VT (Visual Testing) and NDT (Non-Destructive Testing) certified, providing thorough and precise evaluations of weld quality to ensure compliance with industry standards, enhance structural integrity, and detect any defects or weaknesses early."
  },
  {
    id: 9,
    title: "Crane Truck Service",
    icon: Truck,
    description: "Crane truck services in Melbourne and across parts of Victoria provide essential support for a wide range of industries, offering reliable and efficient transport and lifting solutions for heavy or oversized loads, construction materials, machinery, and equipment."
  },
  {
    id: 10,
    title: "Mezzanine Floor and Stair Stringers",
    icon: Layers,
    description: "Expert fabrication and installation of mezzanine floors and stair stringers, providing additional space solutions and structural support for commercial and industrial facilities with precision engineering and quality materials."
  }
];

export const ServicesPage = () => {
  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section 
        data-testid="services-hero"
        className="bg-steel-dark py-20"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-body text-primary font-medium tracking-wide mb-4 animate-fade-in">
            Our Expertise
          </p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-4 animate-slide-in-bottom">
            Specialised & Experienced In
          </h1>
          <p className="font-body text-slate-400 text-base sm:text-lg max-w-2xl animate-fade-in" style={{ animationDelay: '100ms' }}>
            With over 13 years of industry expertise, we deliver comprehensive steel fabrication solutions tailored to your unique project requirements.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section 
        data-testid="services-grid"
        className="py-24 bg-background"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="bg-white p-8 border border-slate-200 hover:border-primary transition-all duration-300 group"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="mb-6">
                  <div className="w-14 h-14 flex items-center justify-center bg-primary/10 group-hover:bg-primary transition-colors">
                    <service.icon className="w-7 h-7 text-primary group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                </div>
                <h3 className="font-heading font-bold text-xl text-steel-dark uppercase tracking-tight mb-4 group-hover:text-primary transition-colors">
                  {service.title}
                </h3>
                <p className="font-body text-steel-gray text-sm leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section 
        data-testid="why-choose-us"
        className="py-24 bg-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-body text-primary font-medium tracking-wide mb-4">
                Why Choose Us
              </p>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-steel-dark uppercase tracking-tight mb-6">
                Excellence in Every Weld
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary text-white font-heading font-bold text-sm flex-shrink-0">
                    01
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg text-steel-dark uppercase tracking-tight mb-2">
                      AS1554 Certified
                    </h4>
                    <p className="font-body text-steel-gray text-sm">
                      All our welding services strictly adhere to Australian standards for structural steel welding.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary text-white font-heading font-bold text-sm flex-shrink-0">
                    02
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg text-steel-dark uppercase tracking-tight mb-2">
                      Full Service Team
                    </h4>
                    <p className="font-body text-steel-gray text-sm">
                      From design to fabrication to installation, our expert team handles every aspect of your project.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-primary text-white font-heading font-bold text-sm flex-shrink-0">
                    03
                  </div>
                  <div>
                    <h4 className="font-heading font-bold text-lg text-steel-dark uppercase tracking-tight mb-2">
                      Quick Turnaround
                    </h4>
                    <p className="font-body text-steel-gray text-sm">
                      We coordinate efficiently with contractors and trades to keep your project on time and within budget.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_precision-steel-2/artifacts/kwszlrgn_architectural-structural-steelwork-compressor.jpg" 
                alt="Steel structure" 
                className="w-full h-48 object-cover border border-slate-200"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_precision-steel-2/artifacts/jmyf48r0_WhatsApp-Image-2025-07-25-at-6.10.30-AM-1.jpeg" 
                alt="Curved steel" 
                className="w-full h-48 object-cover border border-slate-200"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_precision-steel-2/artifacts/il74h3p0_1653486171198.jpeg" 
                alt="Steel frame" 
                className="w-full h-48 object-cover border border-slate-200 col-span-2"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="services-cta"
        className="py-20 bg-primary"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-white uppercase tracking-tight mb-4">
            Need a Custom Solution?
          </h2>
          <p className="font-body text-white/80 text-base mb-8 max-w-2xl mx-auto">
            Every project is unique. Contact us to discuss your specific requirements and get a tailored quote.
          </p>
          <Link to="/contact">
            <Button 
              data-testid="services-contact-button"
              variant="outline"
              className="font-heading font-bold uppercase tracking-widest text-sm rounded-none px-8 py-6 border-2 border-white text-white hover:bg-white hover:text-primary"
            >
              Get in Touch
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
